import React, { useState } from 'react';
import { X, Plus, Edit2, Trash2, Users } from 'lucide-react';

const GroupManagementModal = ({ apiCall, groups, onClose, onSuccess, showError, showSuccess }) => {
  const [mode, setMode] = useState('list'); // 'list', 'add', 'edit'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    groupName: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Group to delete

  const resetForm = () => {
    setFormData({
      groupName: '',
      contactPerson: '',
      contactPhone: '',
      contactEmail: '',
      notes: ''
    });
    setSelectedGroup(null);
  };

  const handleAdd = () => {
    resetForm();
    setMode('add');
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setFormData({
      groupName: group.GroupName,
      contactPerson: group.ContactPerson || '',
      contactPhone: group.ContactPhone || '',
      contactEmail: group.ContactEmail || '',
      notes: group.Notes || ''
    });
    setMode('edit');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.groupName) {
      showError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      
      if (mode === 'add') {
        await apiCall('/bus/rental-groups', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        showSuccess('Group created successfully');
      } else {
        await apiCall(`/bus/rental-groups/${selectedGroup.GroupID}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        showSuccess('Group updated successfully');
      }
      
      resetForm();
      setMode('list');
      onSuccess();
    } catch (error) {
      showError('Error saving group: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    setDeleteConfirm(groupId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setLoading(true);
      await apiCall(`/bus/rental-groups/${deleteConfirm}`, {
        method: 'DELETE'
      });
      showSuccess('Group deleted successfully');
      setDeleteConfirm(null);
      onSuccess();
    } catch (error) {
      showError('Error deleting group: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleCancel = () => {
    resetForm();
    setMode('list');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-6 h-6" />
            Manage Rental Groups
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* LIST MODE */}
          {mode === 'list' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-600">Manage groups that rent buses</p>
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
                >
                  <Plus size={18} />
                  Add Group
                </button>
              </div>

              {groups.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg mb-2">No groups yet</p>
                  <p className="text-sm">Click "Add Group" to create your first rental group</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {groups.map(group => (
                    <div 
                      key={group.GroupID}
                      className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">
                            {group.GroupName}
                          </h3>
                          {group.ContactPerson && (
                            <p className="text-sm text-gray-600 mb-1">
                              <span className="font-medium">Contact:</span> {group.ContactPerson}
                            </p>
                          )}
                          <div className="flex gap-4 text-sm text-gray-500">
                            {group.ContactPhone && (
                              <span>📞 {group.ContactPhone}</span>
                            )}
                            {group.ContactEmail && (
                              <span>✉️ {group.ContactEmail}</span>
                            )}
                          </div>
                          {group.Notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">
                              {group.Notes}
                            </p>
                          )}
                          <div className="mt-3 pt-3 border-t flex gap-4 text-xs">
                            <span className="text-gray-500">
                              Total Trips: <span className="font-semibold text-gray-900">{group.TotalTrips || 0}</span>
                            </span>
                            <span className="text-gray-500">
                              Revenue: <span className="font-semibold text-green-600">
                                {new Intl.NumberFormat('en-US').format(group.TotalRevenue || 0)} $
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEdit(group)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit group"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(group.GroupID)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete group"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ADD/EDIT MODE */}
          {(mode === 'add' || mode === 'edit') && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {mode === 'add' ? 'Add New Group' : 'Edit Group'}
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={formData.groupName}
                  onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                  placeholder="e.g., Scouts Troop 42, Basketball Team"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    placeholder="Name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="03-123456"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Any additional information..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : mode === 'add' ? 'Add Group' : 'Update Group'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this group? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagementModal;